"use strict";

var _interopRequireWildcard = require("@babel/runtime/helpers/interopRequireWildcard").default;
var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault").default;
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.MultiSelectValueContainer = void 0;
var _extends2 = _interopRequireDefault(require("@babel/runtime/helpers/extends"));
var _styled = _interopRequireDefault(require("@emotion/styled"));
var _web = require("@react-hookz/web");
var _lodestarUtils = require("@smartsheet/lodestar-utils");
var _react = _interopRequireWildcard(require("react"));
var _i18n = require("../../../../i18n/i18n");
var _useTheme = require("../../../../theme/utils/useTheme");
var _chip = require("../../../chip");
var _utils = require("../../utils");
var _selectV2Theme = require("../selectV2Theme");
const MultiSelectValueContainerStyled = (0, _styled.default)("div")`
    padding-top: var(--smar--sds--multiselectv2--value-container--padding-top, 6px);
    padding-right: var(--smar--sds--multiselectv2--value-container--padding-right, 0px);
    padding-bottom: var(--smar--sds--multiselectv2--value-container--padding-bottom, 6px);
    padding-left: var(--smar--sds--multiselectv2--value-container--padding-left, 6px);
    flex-grow: 1;
    box-sizing: border-box;
    max-height: ${_ref => {
  let {
    maxHeight
  } = _ref;
  return maxHeight;
}}px;
    min-height: ${_ref2 => {
  let {
    minHeight
  } = _ref2;
  return minHeight;
}}px;
    align-content: flex-start;

    // For Chip eliding.
    min-width: 1px;
    overflow: auto;
    display: flex;

    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    // Make sure the Chips properly elide.
    overflow: auto;
`;
const MultiSelectValueContainer = _ref3 => {
  let {
    activeIndex,
    children,
    className,
    clientId,
    getSelectedItemProps,
    id,
    isReadonly = false,
    minHeight,
    maxSelectedRows,
    rollupSelectionAfter,
    numSelected,
    onHeightChange,
    optionToString,
    removeSelectedItem,
    selectedItems,
    setActiveIndex,
    setSelectedItems,
    onChipClick,
    onChipNavigation,
    formatSelectedValue
  } = _ref3;
  const {
    i18n
  } = (0, _i18n.useI18n)();
  const {
    key
  } = (0, _useTheme.useTheme)();
  const themeMode = key;
  const [chipIdPrefix] = (0, _react.useState)(() => (0, _lodestarUtils.genId)("lodestar-ms-chip"));
  const containerRef = (0, _react.useRef)(null);
  const isInteractive = !isReadonly;
  const containerElement = containerRef.current;
  (0, _react.useEffect)(() => {
    if (window.ResizeObserver && containerElement) {
      const observer = new ResizeObserver(entry => {
        const newVerticalSize = entry[0]?.contentBoxSize[0]?.blockSize || 0;
        onHeightChange(newVerticalSize);
      });
      observer.observe(containerElement);
      return () => {
        observer.disconnect();
      };
    }
  }, [containerElement, onHeightChange]);

  /*
  if (maxRows === 1) show 1 row
  if (maxRows === 2) show 1.5 rows
  if (maxRows === 3) show 2.5 rows
  ... on and on ...
  */
  const chipHeight = 28;
  const maxHeight = chipHeight + 8 + (maxSelectedRows > 1 ? chipHeight / 2 + (maxSelectedRows - 2) * (chipHeight + 4) : 0);

  // Only apply these props when we actually have children, otherwise the axe scanners
  // tell us that:
  // 1: a list box MUST have children.
  // 2: a div without a role shouldn't have a label.
  const includeAriaProps = numSelected > 0;
  const ariaProps = (0, _react.useMemo)(() => ({
    ...(includeAriaProps ? {
      "aria-label": i18n._( /*i18n*/{
        comment: "This is a label referring to all the items the user has selected",
        id: "Selected Items"
      }),
      role: "listbox"
    } : undefined)
  }), [includeAriaProps, i18n]);
  const selectedItemsToShow = rollupSelectionAfter === undefined ? selectedItems : selectedItems.slice(0, rollupSelectionAfter);
  const selectedValue = selectedItems.length - selectedItemsToShow.length;
  const handleChipNavigation = e => {
    e.preventDefault();
    switch (e.key) {
      case "Tab":
        setActiveIndex(-1);
        onChipNavigation("tab");
        break;
      case "ArrowDown":
        if (isInteractive) {
          onChipNavigation("down");
        }
        break;
      case "ArrowUp":
        if (isInteractive) {
          onChipNavigation("up");
        }
        break;
    }
  };
  const selectionLength = selectedItems.length;
  const previousSelectionLength = (0, _web.usePrevious)(selectionLength);
  const [hasFocus, setHasFocus] = (0, _react.useState)(false);

  // If a chip previously had focus and the previously focused chip was deleted, we'll need
  // to move focus to a neighboring chip.
  (0, _react.useEffect)(() => {
    if (hasFocus && previousSelectionLength !== selectionLength && activeIndex >= 0) {
      Array.from(containerRef.current?.childNodes || [])[activeIndex]?.focus();
    }
  }, [hasFocus, activeIndex, previousSelectionLength, selectionLength]);
  return /*#__PURE__*/_react.default.createElement(MultiSelectValueContainerStyled, (0, _extends2.default)({
    maxHeight: maxHeight,
    minHeight: minHeight,
    ref: containerRef,
    onFocus: () => setHasFocus(true),
    onBlur: () => setHasFocus(false),
    className: className
  }, ariaProps, {
    themeMode: themeMode,
    "data-client-id": `${clientId ?? ""}-value-container`
  }), selectedItemsToShow.map((option, index) => {
    const asString = optionToString(option);
    const chipId = id ? `${id}--selected-item-${index}` : `${chipIdPrefix}_${index}`;
    const chipClientId = (0, _lodestarUtils.getClientId)(clientId, "-selected-item");
    const chipProps = {
      onRemove: isInteractive ? e => {
        e.stopPropagation();
        removeSelectedItem(option);
      } : undefined,
      // Do we need this still?
      isFocused: activeIndex === index,
      isReadonly,
      children: null,
      key: asString,
      clientId: chipClientId,
      ...getSelectedItemProps({
        index,
        role: "option",
        selectedItem: option,
        id: chipId,
        onClick: onChipClick ? onChipClick : _utils.stopPropagationHandler,
        onKeyDown: handleChipNavigation
      })
    };
    if (formatSelectedValue) return formatSelectedValue(option, chipProps, false, 0);
    return option.avatarProps ?
    /*#__PURE__*/
    // We're good. key is part of chipProps.
    // eslint-disable-next-line react/jsx-key
    _react.default.createElement(_chip.AvatarChip, (0, _extends2.default)({}, chipProps, {
      avatarProps: option.avatarProps
    }), asString) : /*#__PURE__*/_react.default.createElement(_chip.Chip, (0, _extends2.default)({}, chipProps, {
      color: "neutral",
      size: _selectV2Theme.selectV2Theme.chip.size(themeMode)
    }), asString);
  }), selectedItemsToShow.length < selectedItems.length && (formatSelectedValue !== undefined ? formatSelectedValue(null, {
    onRemove: isInteractive ? e => {
      e.stopPropagation();
      setActiveIndex(selectedItemsToShow.length - 1);
      setSelectedItems(selectedItemsToShow);
    } : undefined,
    children: null,
    ...getSelectedItemProps({
      index: selectedItemsToShow.length,
      role: "option",
      selectedItem: undefined,
      id: `${chipIdPrefix}_${selectedItemsToShow.length}`,
      onClick: onChipClick ? onChipClick : _utils.stopPropagationHandler
    })
  }, true, selectedValue) : /*#__PURE__*/_react.default.createElement(_chip.Chip, (0, _extends2.default)({
    isReadonly: true,
    onRemove: isInteractive ? e => {
      e.stopPropagation();
      setActiveIndex(selectedItemsToShow.length - 1);
      setSelectedItems(selectedItemsToShow);
    } : undefined
  }, getSelectedItemProps({
    index: selectedItemsToShow.length,
    role: "option",
    selectedItem: undefined,
    id: `${chipIdPrefix}_${selectedItemsToShow.length}`,
    onClick: onChipClick ? onChipClick : _utils.stopPropagationHandler
  })), i18n._( /*i18n*/{
    comment: "This is showing the number of selected items",
    id: `{selectedValue} selected`,
    values: {
      selectedValue
    }
  }))), children);
};
exports.MultiSelectValueContainer = MultiSelectValueContainer;
//# sourceMappingURL=MultiSelectValueContainer.js.map